import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Car, Plus, Trash, PencilSimple, Gauge, Wrench, ShieldCheck, WarningCircle, CaretDown } from "@phosphor-icons/react";

const STATUS = {
  activ: { label: "Activ", cls: "bg-emerald-500/15 text-emerald-300" },
  in_service: { label: "În service", cls: "bg-amber-500/15 text-amber-300" },
  casat: { label: "Casat", cls: "bg-white/5 text-muted-foreground" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const daysUntil = (iso) => { if (!iso) return null; return Math.ceil((new Date(iso.slice(0, 10)) - new Date()) / 86400000); };

function ExpiryBadge({ label, date }) {
  const d = daysUntil(date);
  if (d === null) return <span className="rounded bg-white/5 px-2 py-0.5 text-[11px] text-muted-foreground">{label} —</span>;
  const cls = d < 0 ? "bg-red-500/15 text-red-400" : d <= 30 ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/10 text-emerald-300";
  return <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${cls}`} title={fmt(date)}>{label}: {d < 0 ? "expirat" : `${d}z`}</span>;
}

const EMPTY = { plate: "", make: "", model: "", year: "", vin: "", km: 0, rca_expiry: "", casco_expiry: "", itp_expiry: "", status: "activ", assigned_to: "", notes: "" };

function VehicleDialog({ open, onClose, onSaved, editing, users }) {
  const [v, setV] = useState(EMPTY);
  useEffect(() => { setV(editing ? { ...EMPTY, ...editing, assigned_to: editing.assigned_to || "" } : EMPTY); }, [editing, open]);
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const save = async () => {
    try {
      const payload = { ...v, assigned_to: v.assigned_to || null };
      if (editing) await api.put(`/fleet/vehicles/${editing.id}`, payload);
      else await api.post("/fleet/vehicles", payload);
      toast.success(editing ? "Mașină actualizată" : "Mașină adăugată"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Editează mașina" : "Mașină nouă"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Nr. înmatriculare</label><Input value={v.plate} onChange={(e) => set("plate", e.target.value)} data-testid="veh-plate" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">An</label><Input type="number" value={v.year} onChange={(e) => set("year", e.target.value)} data-testid="veh-year" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Marcă</label><Input value={v.make} onChange={(e) => set("make", e.target.value)} data-testid="veh-make" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Model</label><Input value={v.model} onChange={(e) => set("model", e.target.value)} data-testid="veh-model" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Km</label><Input type="number" value={v.km} onChange={(e) => set("km", e.target.value)} data-testid="veh-km" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">VIN</label><Input value={v.vin} onChange={(e) => set("vin", e.target.value)} /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">RCA expiră</label><Input type="date" value={(v.rca_expiry || "").slice(0, 10)} onChange={(e) => set("rca_expiry", e.target.value)} data-testid="veh-rca" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">CASCO expiră</label><Input type="date" value={(v.casco_expiry || "").slice(0, 10)} onChange={(e) => set("casco_expiry", e.target.value)} data-testid="veh-casco" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">ITP expiră</label><Input type="date" value={(v.itp_expiry || "").slice(0, 10)} onChange={(e) => set("itp_expiry", e.target.value)} data-testid="veh-itp" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Status</label>
            <Select value={v.status} onValueChange={(val) => set("status", val)}>
              <SelectTrigger data-testid="veh-status"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Responsabil</label>
            <Select value={v.assigned_to || "none"} onValueChange={(val) => set("assigned_to", val === "none" ? "" : val)}>
              <SelectTrigger data-testid="veh-assigned"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="max-h-64"><SelectItem value="none">— nealocat —</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Note</label><Textarea value={v.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="veh-save-btn">{editing ? "Salvează" : "Adaugă"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ServiceDialog({ vehicle, open, onClose, onSaved }) {
  const [s, setS] = useState({ type: "revizie", date: "", km: 0, cost: 0, garage: "", description: "" });
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try { await api.post(`/fleet/vehicles/${vehicle.id}/service`, s); toast.success("Intervenție adăugată"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Revizie / reparație — {vehicle?.plate}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Tip</label>
            <Select value={s.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="svc-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="revizie">Revizie</SelectItem><SelectItem value="reparatie">Reparație</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Data</label><Input type="date" value={s.date} onChange={(e) => set("date", e.target.value)} data-testid="svc-date" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Km</label><Input type="number" value={s.km} onChange={(e) => set("km", e.target.value)} /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Cost (lei)</label><Input type="number" value={s.cost} onChange={(e) => set("cost", e.target.value)} data-testid="svc-cost" /></div>
          <div className="col-span-2 grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Service/atelier</label><Input value={s.garage} onChange={(e) => set("garage", e.target.value)} /></div>
          <div className="col-span-2 grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Descriere</label><Textarea value={s.description} onChange={(e) => set("description", e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="svc-save-btn">Adaugă</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VehicleCard({ v, users, canEdit, onEdit, onDelete, onReload }) {
  const [open, setOpen] = useState(false);
  const [kmVal, setKmVal] = useState(v.km);
  const [svcOpen, setSvcOpen] = useState(false);
  const st = STATUS[v.status] || STATUS.activ;
  const owner = users.find((u) => u.id === v.assigned_to)?.name;
  const kmDays = daysUntil(v.km_updated_at) === null ? null : -daysUntil(v.km_updated_at);
  const saveKm = async () => { try { await api.post(`/fleet/vehicles/${v.id}/km`, { km: Number(kmVal) || 0 }); toast.success("Km actualizați"); onReload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid={`vehicle-${v.id}`}>
      <div className="flex flex-wrap items-center gap-4 p-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400"><Car size={22} weight="bold" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-head text-lg font-bold tracking-wide">{v.plate}</span>
            <span className="text-sm text-muted-foreground">{v.make} {v.model} {v.year ? `· ${v.year}` : ""}</span>
            <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${st.cls}`}>{st.label}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <ExpiryBadge label="RCA" date={v.rca_expiry} />
            <ExpiryBadge label="CASCO" date={v.casco_expiry} />
            <ExpiryBadge label="ITP" date={v.itp_expiry} />
            {owner && <span className="rounded bg-white/5 px-2 py-0.5 text-[11px] text-muted-foreground">{owner}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 font-head text-lg font-extrabold tabular-nums"><Gauge size={16} /> {(v.km || 0).toLocaleString("ro-RO")} km</div>
          <div className={`text-[11px] ${kmDays != null && kmDays >= 90 ? "text-amber-400" : "text-muted-foreground"}`}>{kmDays != null ? `actualizat acum ${kmDays}z` : "—"}</div>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-white/5"><CaretDown size={16} className={open ? "rotate-180 transition-transform" : "transition-transform"} /></button>
      </div>

      {open && (
        <div className="border-t border-border p-4">
          {canEdit && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase text-muted-foreground">Actualizează km:</span>
              <Input type="number" className="h-8 w-32" value={kmVal} onChange={(e) => setKmVal(e.target.value)} data-testid={`km-input-${v.id}`} />
              <Button size="sm" variant="outline" onClick={saveKm} data-testid={`km-save-${v.id}`}><Gauge size={14} className="mr-1" /> Salvează km</Button>
              <Button size="sm" variant="outline" onClick={() => setSvcOpen(true)} data-testid={`svc-add-${v.id}`}><Wrench size={14} className="mr-1" /> Revizie/Reparație</Button>
              <Button size="sm" variant="ghost" onClick={() => onEdit(v)} data-testid={`veh-edit-${v.id}`}><PencilSimple size={15} /></Button>
              <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onDelete(v.id)} data-testid={`veh-del-${v.id}`}><Trash size={15} /></Button>
            </div>
          )}
          <h4 className="mb-2 text-sm font-bold">Istoric revizii & reparații</h4>
          <div className="space-y-1.5">
            {(v.services || []).slice().reverse().map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-sm">
                <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${s.type === "reparatie" ? "bg-red-500/15 text-red-300" : "bg-blue-500/15 text-blue-300"}`}>{s.type === "reparatie" ? "Reparație" : "Revizie"}</span>
                <span>{fmt(s.date)}</span>
                {s.km ? <span className="text-muted-foreground">· {s.km.toLocaleString("ro-RO")} km</span> : null}
                {s.garage && <span className="text-muted-foreground">· {s.garage}</span>}
                {s.description && <span className="text-muted-foreground">· {s.description}</span>}
                {s.cost ? <span className="ml-auto font-semibold text-amber-400">{fmtMoney(s.cost)}</span> : null}
              </div>
            ))}
            {(v.services || []).length === 0 && <p className="text-sm text-muted-foreground">Nicio intervenție înregistrată.</p>}
          </div>
        </div>
      )}
      <ServiceDialog vehicle={v} open={svcOpen} onClose={() => setSvcOpen(false)} onSaved={onReload} />
    </div>
  );
}

export default function Fleet() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("pm", "admin");
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/fleet/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/fleet/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const del = async (id) => { try { await api.delete(`/fleet/vehicles/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 fade-up" data-testid="fleet-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Logistică · Parc Auto</p>
          <h1 className="font-head text-4xl font-extrabold tracking-tighter">Parc Auto</h1>
          <p className="mt-1 text-sm text-muted-foreground">RCA, CASCO, ITP, revizii, reparații, km la zi (actualizare la 3 luni) — cu remindere automate.</p>
        </div>
        {canEdit && <Button onClick={() => { setEditing(null); setDlg(true); }} data-testid="veh-new-btn"><Plus size={16} className="mr-1.5" /> Mașină nouă</Button>}
      </div>

      {alerts.length > 0 && (
        <div className="mb-5 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4" data-testid="fleet-alerts">
          <div className="mb-2 flex items-center gap-2 font-bold text-amber-300"><WarningCircle size={18} weight="bold" /> Alerte parc auto ({alerts.length})</div>
          <div className="flex flex-wrap gap-2">
            {alerts.map((a, i) => (
              <span key={i} className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${a.expired ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-300"}`}>
                {a.plate} · {a.type} {a.type === "KM" ? `neactualizat ${-a.days}z` : (a.expired ? "EXPIRAT" : `${a.days}z`)}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {vehicles.map((v) => <VehicleCard key={v.id} v={v} users={users} canEdit={canEdit} onEdit={(x) => { setEditing(x); setDlg(true); }} onDelete={del} onReload={load} />)}
        {vehicles.length === 0 && <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Niciun autovehicul înregistrat. Adaugă prima mașină din parcul auto.</p>}
      </div>

      <VehicleDialog open={dlg} onClose={() => setDlg(false)} onSaved={load} editing={editing} users={users} />
    </div>
  );
}
